import { useEffect, useState, useCallback } from "react";
import { api, shorten } from "@/lib/api";
import { useWallet } from "@/context/WalletContext";
import { useLang } from "@/context/LanguageContext";
import { SectorPanel } from "@/components/SectorPanel";
import { toast } from "sonner";
import { Search, Loader2, ShoppingCart } from "lucide-react";

export default function Marketplace() {
  const { account, registerSector, refreshProfile } = useWallet();
  const { t } = useLang();
  const [tab, setTab] = useState("primary");
  const [primary, setPrimary] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [panel, setPanel] = useState(null);
  const [buying, setBuying] = useState(null);

  const loadPrimary = useCallback(() => {
    setLoading(true);
    api.post("/sectors/sample?count=24").then((r) => setPrimary(r.data)).finally(() => setLoading(false));
  }, []);
  const loadListings = useCallback(() => {
    setLoading(true);
    const params = {};
    if (query) params.region = query;
    if (maxPrice) params.max_price = maxPrice;
    api.get("/marketplace/listings", { params }).then((r) => setListings(r.data)).finally(() => setLoading(false));
  }, [query, maxPrice]);

  useEffect(() => { tab === "primary" ? loadPrimary() : loadListings(); }, [tab, loadPrimary, loadListings]);

  const buyListing = async (l) => {
    if (!account) { toast.error("Connect wallet to purchase"); return; }
    setBuying(l.sector_id);
    try {
      await api.post("/marketplace/buy", { sector_id: l.sector_id, buyer_wallet: account, payment_simulated: true });
      toast.success("Sector acquired", { description: `${l.sector_id} transferred to your custody.` });
      refreshProfile();
      loadListings();
    } catch (e) { toast.error(e.response?.data?.detail || "Purchase failed"); }
    finally { setBuying(null); }
  };

  const filteredPrimary = primary.filter((s) =>
    !query || s.name.toLowerCase().includes(query.toLowerCase()) || s.sector_id.includes(query));

  return (
    <div className="relative z-10 pt-28 pb-10 max-w-[1500px] mx-auto px-6 md:px-10" data-testid="marketplace">
      <div className="text-overline mb-4">{t.market.tag}</div>
      <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tight">{t.market.title}</h1>
      <p className="mt-6 text-cosmic/70 max-w-2xl font-light leading-relaxed text-lg">
        {t.market.intro}
      </p>

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 mt-10">
        <div className="flex border border-cosmic/10">
          <button data-testid="market-tab-primary" onClick={() => setTab("primary")}
            className={`px-6 py-3 font-mono text-xs tracking-widest uppercase ${tab === "primary" ? "bg-portal text-deepspace" : "text-cosmic/60"}`}>
            {t.market.primary}
          </button>
          <button data-testid="market-tab-resale" onClick={() => setTab("resale")}
            className={`px-6 py-3 font-mono text-xs tracking-widest uppercase ${tab === "resale" ? "bg-portal text-deepspace" : "text-cosmic/60"}`}>
            {t.market.secondary}
          </button>
        </div>
        <div className="flex-1 flex items-center gap-3 border border-cosmic/10 px-4 py-2.5">
          <Search size={15} className="text-cosmic/50" />
          <input data-testid="market-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t.market.search}
            className="bg-transparent outline-none flex-1 font-mono text-sm placeholder:text-cosmic/30" />
        </div>
        {tab === "resale" && (
          <input data-testid="market-maxprice" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}
            placeholder={t.market.maxPrice} type="number"
            className="bg-transparent border border-cosmic/10 px-4 py-2.5 outline-none font-mono text-sm w-32 placeholder:text-cosmic/30" />
        )}
        {tab === "resale" && (
          <button onClick={loadListings} className="btn-ghost py-2.5">{t.market.apply}</button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-portal" /></div>
      ) : tab === "primary" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cosmic/10 border border-cosmic/10 mt-8">
          {filteredPrimary.map((s) => (
            <div key={s.sector_id} data-testid={`market-primary-${s.sector_id}`}
              className="bg-deepspace/40 p-6 hover:bg-portal/5 transition-colors">
              <div className="flex justify-between items-start">
                <div className="font-mono text-portal text-sm">{s.sector_id}</div>
                <div className="font-mono text-[0.65rem] text-portal border border-portal/30 px-2 py-0.5 uppercase">{t.market.available}</div>
              </div>
              <div className="font-grotesk text-lg mt-3">{s.name}</div>
              <div className="font-mono text-xs text-cosmic/60 mt-2">{s.lat_label} · {s.lng_label}</div>
              <div className="flex items-center justify-between mt-6">
                <div className="font-display text-xl">{s.price} <span className="text-xs text-cosmic">USDC</span></div>
                <button data-testid={`register-${s.sector_id}`} onClick={() => setPanel(s.sector_id)}
                  className="btn-portal py-2 px-5 text-[0.7rem]">{t.market.register}</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8">
          {listings.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cosmic/10 border border-cosmic/10">
              {listings.map((l) => (
                <div key={l.sector_id} data-testid={`market-resale-${l.sector_id}`} className="bg-deepspace/40 p-6 hover:bg-portal/5 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="font-mono text-portal text-sm">{l.sector_id}</div>
                    <div className="font-mono text-[0.65rem] text-mars border border-mars/40 px-2 py-0.5 uppercase">{t.market.listed}</div>
                  </div>
                  <div className="font-grotesk text-lg mt-3">{l.name}</div>
                  <div className="font-mono text-xs text-cosmic/60 mt-2">{t.market.seller} · {shorten(l.owner_wallet)}</div>
                  <div className="flex items-center justify-between mt-6">
                    <div className="font-display text-xl">{l.sale_price} <span className="text-xs text-cosmic">USDC</span></div>
                    <button data-testid={`buy-${l.sector_id}`} onClick={() => buyListing(l)} disabled={buying === l.sector_id}
                      className="btn-portal py-2 px-5 text-[0.7rem] flex items-center gap-1 disabled:opacity-50">
                      {buying === l.sector_id ? <Loader2 size={12} className="animate-spin" /> : <ShoppingCart size={12} />} {t.market.acquire}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-cosmic/50 font-light py-10">{t.market.noListings}</p>
          )}
        </div>
      )}

      {panel && (
        <SectorPanel sectorId={panel} onClose={() => setPanel(null)} onRegistered={() => { loadPrimary(); refreshProfile(); }} />
      )}
    </div>
  );
}
